import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { LoadingButton } from '@mui/lab'
import { Box, Stack, Typography } from '@mui/material'
import { useGetAllProjectShearConnections } from '@api/project-shear-connections/project-shear-connections'
import { DenseTable, Section } from '@ui2/structure'
import { Tabs as DetailTabs } from '@ui/navigation'
import { useSnackbar } from '@ui/snackbar'
import { buildErrorMessage } from 'src/constants/errors'
import { ConnectorSelection } from '../../connectorSelection'
import useSaveShearAnchorSelections from '../../hooks/useSaveShearAnchorSelections'
import { shearAnchorLocationOfRowId } from '../../utils'
import ConnectorSelectionSelect from '../ConnectorSelectionSelect'
import Results2DetailCloseButton from '../Results2DetailCloseButton'
import { Results2ListItem } from '../Results2ElementList'
import Results2PaneHeader from '../Results2PaneHeader'

interface Props {
  /** The Schubanker rows selected in the list — the locations the action is applied to. */
  selectedItems: Results2ListItem[]
  /** Disables the action while a list autosave is dirty or in flight. */
  disableForm?: boolean
}

const etaFormatter = new Intl.NumberFormat('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * Multi-edit pane of the Results2 "Schubanker" tab, shown while several Schubanker are selected:
 * fixes their Schubverbinder at once, or hands them back to the prioritization. What they use
 * today is listed below, so the effect is visible before applying it.
 */
const Results2ShearAnchorMultiEditPane = ({
  selectedItems,
  disableForm = false,
}: Props): ReactElement => {
  const { projectId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { data: shearConnections = [] } = useGetAllProjectShearConnections(projectId as string, {
    query: {
      enabled: !!projectId,
      onError: (error: unknown) =>
        enqueueSnackbar(
          buildErrorMessage(error as AxiosError, 'Fehler beim Laden der Schubverbinder'),
          { variant: 'error' },
        ),
    },
  })
  const { saveShearAnchorSelections, isSaving } = useSaveShearAnchorSelections()
  // `null` keeps each Schubanker's Schubverbinder.
  const [selection, setSelection] = useState<ConnectorSelection | null>(null)

  const locations = useMemo(
    () => selectedItems.map(item => shearAnchorLocationOfRowId(item.id)),
    [selectedItems],
  )

  const handleApply = () => {
    if (!selection) return
    saveShearAnchorSelections(locations, selection, () => setSelection(null))
  }

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ flexShrink: 0, position: 'relative' }}>
        <DetailTabs
          items={[{ id: 'multi-edit', label: 'Mehrfachbearbeitung' }]}
          value="multi-edit"
          size="md"
          kind="folder"
        />
        <Results2DetailCloseButton />
      </Box>

      <Results2PaneHeader
        title={`${selectedItems.length} Schubanker`}
        subtitle="Änderungen gelten für alle ausgewählten Schubanker"
      />

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2 }}>
        <Stack spacing={2.5} sx={{ py: 1.5 }}>
          <Section title="Schubanker festlegen" hideBorderBottom>
            <Stack spacing={1} sx={{ pb: 1 }}>
              <ConnectorSelectionSelect
                value={selection}
                onChange={setSelection}
                connections={shearConnections}
                label="Schubanker"
                dataCy="shear-anchor-multi-connector-selection"
                disabled={isSaving || disableForm}
                offerUnchanged
              />
              <Typography variant="caption" color="text.secondary">
                „Automatisch“ überlässt die Wahl wieder der Priorisierung. Ein festgelegter
                Schubverbinder wird auch nachgewiesen, wenn er nicht ausreicht.
              </Typography>
            </Stack>
          </Section>

          <Section title="Ausgewählte Schubanker" hideBorderBottom>
            <Box sx={{ pb: 1 }}>
              <DenseTable
                headers={['Schubanker', 'Schubverbinder', 'η']}
                columnAlign={['left', 'left', 'right']}
                rows={selectedItems.map(item => [
                  item.label ?? item.id,
                  <Box key="connector" sx={{ whiteSpace: 'nowrap' }}>
                    {item.qs ?? '–'}
                    {item.qsManual ? ' (manuell)' : ''}
                  </Box>,
                  item.eta === undefined ? '–' : etaFormatter.format(item.eta),
                ])}
                emptyMessage="Keine Schubanker ausgewählt"
              />
            </Box>
          </Section>
        </Stack>
      </Box>

      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ flexShrink: 0, px: 2, py: 1, borderTop: 1, borderColor: 'divider' }}
      >
        <Typography variant="caption" color="text.secondary">
          {selection ? 'Schubverbinder wird übernommen' : 'Nichts einzutragen'}
        </Typography>
        <LoadingButton
          size="small"
          variant="contained"
          onClick={handleApply}
          disabled={!selection || disableForm}
          loading={isSaving}
          data-cy="shear-anchor-multi-apply-btn"
        >
          Auf {selectedItems.length} Schubanker anwenden
        </LoadingButton>
      </Stack>
    </Box>
  )
}

export default Results2ShearAnchorMultiEditPane
